import React, { useEffect, useMemo, useState } from "react";
import ContentArea from "../ContentArea/ContentArea";
import Layout from "./Layout/Layout";
import StatusMessage from "../StatusMessage/StatusMessage";
import LineChart, { LineChartDatum, LineChartMarker, LineChartTick } from "../LineChart/LineChart";
import StatusDataLoader from "../../services/StatusDataLoader";
import BlockCalculator from "../../utils/Calculators/InformationCalculator";
import {
  buildWeeklyInflationSeries,
  getGenesisDate,
  WeeklyInflationPoint,
} from "../../utils/Calculators/InflationCalculator";
import { useSeo } from "../../utils/Hooks/useSeo";
import "./InflationPage.css";

const YEARS_FORWARD = 15;
const SEO_DESCRIPTION =
  "Visualize Bitcoin annual inflation over time and projected future path using weekly historical and forward schedule calculations.";

const formatInflation = (value: number, decimals = 2) => `${value.toFixed(decimals)} %`;

const formatDate = (date: Date) =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);

const buildFiveYearTicks = (start: Date, end: Date): LineChartTick[] => {
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();
  const ticks: LineChartTick[] = [];

  const firstTickYear = Math.ceil(startYear / 5) * 5;
  for (let year = firstTickYear; year <= endYear; year += 5) {
    const tickDate = new Date(Date.UTC(year, 0, 1));
    if (tickDate.getTime() < start.getTime() || tickDate.getTime() > end.getTime()) {
      continue;
    }
    ticks.push({ date: tickDate, label: `${year}` });
  }

  return ticks;
};

export const InflationPage = () => {
  const [loading, setLoading] = useState(true);
  const [currentInflation, setCurrentInflation] = useState<number | undefined>(undefined);

  useSeo({
    title: "Bitcoin Inflation",
    description: SEO_DESCRIPTION,
    canonicalPath: "/inflation",
  });

  useEffect(() => {
    const load = async () => {
      const status = await StatusDataLoader.getStatusData();
      const currentBlock = status?.network?.currentBlock ?? 0;
      const inflation = BlockCalculator.calculateAnnualInflationRate(currentBlock);
      setCurrentInflation(inflation);
      setLoading(false);
    };

    load();
  }, []);

  const { chartData, markers, ticks } = useMemo(() => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setUTCFullYear(endDate.getUTCFullYear() + YEARS_FORWARD);

    const series: WeeklyInflationPoint[] = buildWeeklyInflationSeries(endDate);
    const chartData: LineChartDatum[] = series.map((point) => ({
      date: point.date,
      value: point.annualInflationRate,
    }));

    const markers: LineChartMarker[] = [
      { date: today, label: "Today", tone: "accent" },
    ];

    const ticks = buildFiveYearTicks(getGenesisDate(), endDate);

    return { chartData, markers, ticks };
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="progress-circle">
          <progress className="pure-material-progress-circular" />
        </div>
      );
    }

    if (!chartData.length) {
      return (
        <StatusMessage
          title="We could not calculate inflation history."
          description="Please try again in a minute."
          minHeightRem={24}
        />
      );
    }

    return (
      <div className="content-lines">
        <section className="inflation-overview-card">
          <div className="inflation-overview-top">
            <div>
              <h1 className="inflation-overview-title">Bitcoin Inflation</h1>
              <div className="inflation-overview-subtitle">{SEO_DESCRIPTION}</div>
            </div>
            <div className="inflation-current-panel">
              <div className="inflation-current-label">Current annual rate</div>
              <div className="inflation-current-value">
                {currentInflation !== undefined
                  ? formatInflation(currentInflation, 4)
                  : "--"}
              </div>
            </div>
          </div>
        </section>

        <div className="inflation-chart-wrap">
          <LineChart
            data={chartData}
            widthRem={60}
            heightRem={24}
            title="Bitcoin Annual Inflation"
            xAxisLabel="Date"
            yAxisLabel="Annual inflation rate"
            valueFormatter={formatInflation}
            yAxisValueFormatter={formatInflation}
            dateFormatter={formatDate}
            markers={markers}
            xTicks={ticks}
            yScale="log"
          />
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <ContentArea isTopArea>
        {renderContent()}
      </ContentArea>
    </Layout>
  );
};
